import {
    ChipGridInfo,
    ChipModel,
    NewChipPayload,
} from '@app/models/chip.model';
import { Injectable, inject } from '@angular/core';
import { Observable, from, map, switchMap } from 'rxjs';

import { AuthService } from '@app/user/services/auth.service';
import { ChipDetailsResponse } from '@app/shared/models/chip-details.model';
import { Code15 } from '@app/models/chip-code.type';
import { HttpClient } from '@angular/common/http';
import { PostgrestSingleResponse } from '@supabase/supabase-js';
import { SupabaseClientService } from '@app/services/supabase.client.service';
import { ToastService } from '@app/services/toast.service';
import { environment } from '@env/environment';
import { toCamelCase } from '@shared/helpers/camel-case-converter.helper';

@Injectable()
export class ChipService {
    private supabaseClient = inject(SupabaseClientService);
    private http = inject(HttpClient);
    private authService = inject(AuthService);
    private toastService = inject(ToastService);

    private registrationPdfUpload =
        'https://ctvmngvzsmtrbqckpgxq.supabase.co/functions/v1/registration-chip-pdf';

    getChipSearchByCode(code: Code15): Observable<ChipModel[]> {
        return from(
            this.supabaseClient.client
                .from('chip')
                .select('id, code, lost, stolen, dead')
                .eq('code', code)
                .then(({ data, error }) => {
                    if (error) {
                        throw error;
                    }
                    return data;
                }),
        );
    }

    getChipDetailInfo(code: Code15): Observable<ChipDetailsResponse> {
        return from(
            this.supabaseClient.client
                .from('chip')
                .select(
                    `
                    id,
                    code,
                    registration_unique_id,
                    chip_action (
                        id,
                        is_email_sent,
                        pdf_url,
                        date,
                        owner_signature,
                        veterinarian_signature,
                        previous_owner_signature,
                        veterinarians (
                            id,
                            name,
                            surname,
                            veterinaryId
                        ),
                        owner (
                            id,
                            email,
                            phone,
                            id_type,
                            id_number,
                            full_name,
                            addresses (
                                id,
                                address,
                                country_id,
                                region_id,
                                settlement_id,
                                zip_code
                            )
                        ),
                        animal (
                            id,
                            type,
                            breed,
                            name,
                            sex,
                            date_of_birth,
                            chip_location,
                            castrated,
                            coat_type,
                            size,
                            addresses (
                                id,
                                address,
                                country_id,
                                region_id,
                                settlement_id,
                                zip_code
                            )
                        )
                   )
                `,
                )
                .eq('code', code)
                .single()
                .then(({ data, error }) => {
                    if (error) {
                        throw error;
                    }

                    return this.mapChipDetailsResponse(data);
                }),
        );
    }

    mapChipDetailsResponse(data: any): ChipDetailsResponse {
        const { chipAction, ...codeInfo } = toCamelCase(data);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { owner, animal, veterinarians, ...chipInfo } = chipAction;
        const chipDetails: ChipDetailsResponse = {
            codeInfo,
            owner,
            animal,
            veterinarian: chipAction.veterinarians,
            registrationDetails: {
                isEmailSent: chipAction.isEmailSent,
                date: chipAction.date,
                pdfUrl: chipAction.pdfUrl,
                ...chipInfo
            },
        };
        return chipDetails;
    }

    async getAllRegisteredChips(): Promise<ChipGridInfo[]> {
        const { data, error } = await this.supabaseClient.client
            .from('chip')
            .select(
                `
                id,
                code,
                lost,
                stolen,
                chip_action ( is_email_sent, pdf_url, date )
            `,
            )
            .eq('created_by', this.authService.getUserId())
            .order('created_at', { ascending: false });
        if (error) {
            this.toastService.error(error?.message);
            throw error;
        }
        return toCamelCase(data);
    }

    registerNewChip(payload: NewChipPayload): Observable<ChipDetailsResponse> {
        return from(this.supabaseClient.client.rpc('register_new_chip', { payload })).pipe(
            map(({ data, error }: PostgrestSingleResponse<ChipDetailsResponse>) => {
                if (error) {
                    throw error;
                }
                return toCamelCase(data);
            }),
        );
    }

    chipChangeOwner(payload: NewChipPayload): Observable<ChipDetailsResponse> {
        return from(this.supabaseClient.client.rpc('change_owner', { payload })).pipe(
            map(({ data, error }: PostgrestSingleResponse<ChipDetailsResponse>) => {
                if (error) {
                    throw error;
                }
                return toCamelCase(data);
            }),
        );
    }


    chipChangeAddress(payload: NewChipPayload): Observable<ChipDetailsResponse> {
        return from(this.supabaseClient.client.rpc('change_address', { payload })).pipe(
            map(({ data, error }: PostgrestSingleResponse<ChipDetailsResponse>) => {
                if (error) {
                    throw error;
                }
                return toCamelCase(data);
            }),
        );
    }

    uploadPdf(pdfBlob: string, chipRegistration: ChipDetailsResponse): any {
        return from(this.supabaseClient.client?.auth.getSession()).pipe(
            switchMap((session) => {
                return this.http.post<{ pdfUrl: string }>(
                    this.registrationPdfUpload,
                    this.buildFormData(chipRegistration, pdfBlob, 'registation', null),
                    {
                        headers: {
                            Authorization: `Bearer ${session.data.session?.access_token}`,
                            apikey: environment.supabaseKey,
                        },
                    },
                );
            }),
        );
    }

    uploadPdfChangeOwner(pdfBlob: string, previousOwnerEmail: string, chipRegistration: ChipDetailsResponse): any {
        return from(this.supabaseClient.client?.auth.getSession()).pipe(
            switchMap((session) => {
                return this.http.post<{ pdfUrl: string }>(
                    this.registrationPdfUpload,
                    this.buildFormData(chipRegistration, pdfBlob, 'change-owner', previousOwnerEmail),
                    {
                        headers: {
                            Authorization: `Bearer ${session.data.session?.access_token}`,
                            apikey: environment.supabaseKey,
                        },
                    },
                );
            }),
        );
    }

    updateChipInformation(chip: ChipModel): Observable<ChipModel> {
        return from(
            this.supabaseClient.client
                .from('chip')
                .update(chip)
                .eq('id', chip.id)
                .select('id, code, lost, stolen, dead')
                .single(),
        ).pipe(
            map(({ data, error }) => {
                if (error) {
                    throw error;
                }
                return toCamelCase(data);
            }),
        );
    }

    private buildFormData(
        { owner, registrationDetails }: ChipDetailsResponse,
        pdfBlob: string,
        type: 'registation' | 'change-owner',
        previousOwnerEmail: string | null
    ): FormData {
        const formData = new FormData();
        formData.append('pdf', pdfBlob);
        formData.append('fileName', `${type}-${registrationDetails.id}.pdf`);
        formData.append('ownerEmail', owner.email);
        formData.append('registrationId', registrationDetails.id);
        if (type === 'change-owner' && previousOwnerEmail) {
            formData.append('previousOwnerEmail', previousOwnerEmail);
        }
        return formData;
    }
}
